import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  confirmAction,
  loginAction,
  loginWithGoogleAccountAction,
} from "../../redux/slices/login-slice";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { InputContainer, LoginContainer } from "./styled";
import BarekamButton, {
  AuthButtons,
  SimpleButtons,
} from "../components/button";
import { useLocation, useNavigate } from "react-router-dom";
import BarekamText from "../components/text";
import BarekamInput from "../components/input";
import { Divider } from "@mui/material";
import { Message, MessageSections } from "../components/message/message";
import { ToastContainer } from "react-toastify";
import { Footer } from "../footer/footer";
import { getMessage, initToast } from "../shared/helper-functions";
import { registerWithGoogleAccountAction } from "../../redux/slices/register-slice";

const Login = () => {
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const { errorMessage } = useAppSelector((state) => state.loginReducer);

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const locale = location.pathname.split("/")[1];

  useEffect(() => {
    const toastMessage = state?.toastMessage;
    const type = state?.toastType;
    if (toastMessage) {
      initToast(toastMessage, type);
      state.toastMessage = null;
      state.toastType = null;
    }

    if (queryParams.get("ct")) {
      dispatch(confirmAction(queryParams.get("ct") || "")).then((confirmed) => {
        // if (confirmed.payload && confirmed.payload.response && confirmed.payload.response.status !== 200) {
        //     initToast(sectionMessages[confirmed.payload.response.data] ? sectionMessages[confirmed.payload.response.data].name : 'not_confirmed', 'error');
        // } else {
        //     initToast(sectionMessages['confirmed'] ? sectionMessages['confirmed'].name : 'confirmed', 'success');
        // }
      });
    }
  }, [navigate, state]);

  const signIn = async () => {
    dispatch(loginAction(loginData)).then(() => {
      const accessToken = localStorage.getItem("access_token");

      if (accessToken) {
        navigate("../orders/my");
      }
    });
  };

  function onEmailChange(value: string) {
    setLoginData((state) => {
      return { ...state, email: value };
    });
  }

  async function googleAuthSuccess(credentialResponse: CredentialResponse) {
    try {
      dispatch(
        registerWithGoogleAccountAction({
          credentials: credentialResponse?.credential,
          languageId,
        }),
      ).then((registrationData) => {
        const response = registrationData.payload as {
          userId: number;
          error: any;
        };

        if (response.userId) {
          try {
            dispatch(
              loginWithGoogleAccountAction(credentialResponse?.credential),
            ).then(() => {
              const accessToken = localStorage.getItem("access_token");

              if (accessToken) {
                navigate("../orders/my", {
                  state: {
                    toastMessage: getMessage(
                      "register_succeeded",
                      MessageSections.ORDERS,
                      messageCache,
                      languageId,
                    ),
                    toastType: "success",
                  },
                });
              }
            });
          } catch (error) {
            console.error("Failed to login / sign up with the Google account!");
          }
        } else {
          if (
            response?.error?.response?.data.find(
              (item: any) =>
                (item["message"] as string) === "email_is_not_unique",
            )
          ) {
            try {
              dispatch(
                loginWithGoogleAccountAction(credentialResponse?.credential),
              ).then(() => {
                const accessToken = localStorage.getItem("access_token");

                if (accessToken) {
                  navigate("../orders/my", {
                    state: {
                      toastMessage: getMessage(
                        "register_succeeded",
                        MessageSections.ORDERS,
                        messageCache,
                        languageId,
                      ),
                      toastType: "success",
                    },
                  });
                }
              });
            } catch (error) {
              console.error(
                "Failed to login / sign up with the Google account!",
              );
            }
          } else {
            console.error(response.error);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  function googleAuthError() {
    console.error("Failed to login / sign up with the Google account!");
  }

  function onPasswordChange(value: string) {
    setLoginData((state) => {
      return { ...state, password: value };
    });
  }

  return (
    <>
      <LoginContainer>
        <ToastContainer />

        <InputContainer>
          <BarekamText variant={"xxl"} align={"center"} bold>
            <Message messageKey={"sign_in"} sectionId={MessageSections.LOGIN} />
          </BarekamText>

          <BarekamInput
            key="login-email"
            customInputVariant={"standard"}
            placeholder={getMessage(
              "email",
              MessageSections.LOGIN,
              messageCache,
              languageId,
            )}
            value={loginData?.email}
            onChange={(event) => onEmailChange(event.target.value)}
            onKeyUp={(event) => event.key === "Enter" && signIn()}
            error={!!errorMessage}
            width={"100%"}
            required
            validatePattern
          />
          <BarekamInput
            key="login-password"
            customInputVariant={"standard"}
            placeholder={getMessage(
              "password",
              MessageSections.LOGIN,
              messageCache,
              languageId,
            )}
            value={loginData?.password}
            onChange={(event) => onPasswordChange(event.target.value)}
            onKeyUp={(event) => event.key === "Enter" && signIn()}
            type={"password"}
            error={!!errorMessage}
            width={"100%"}
            errorMessage={
              errorMessage &&
              getMessage(
                errorMessage,
                MessageSections.LOGIN,
                messageCache,
                languageId,
              )
            }
            required
          />

          <div style={{ width: "100%" }}>
            <BarekamButton
              type={SimpleButtons.LINK}
              onClick={() => navigate("../recover")}
            >
              <BarekamText variant={"m"}>
                <Message
                  messageKey={"forgot"}
                  sectionId={MessageSections.LOGIN}
                />
              </BarekamText>
            </BarekamButton>
          </div>

          <BarekamButton type={AuthButtons.MAIN} size={"m"} onClick={signIn}>
            <BarekamText variant={"l"} fontWeight={700} align={"center"}>
              <Message
                messageKey={"sign_in"}
                sectionId={MessageSections.LOGIN}
              />
            </BarekamText>
          </BarekamButton>

          <Divider style={{ width: "100%" }}>
            <BarekamText variant={"m"} opaque>
              <Message messageKey={"or"} sectionId={MessageSections.LOGIN} />
            </BarekamText>
          </Divider>

          <GoogleLogin
            onSuccess={googleAuthSuccess}
            onError={googleAuthError}
            width={500}
            locale={locale}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BarekamText variant={"m"} style={{ marginRight: "0.3rem" }}>
              <Message
                messageKey={"no_account"}
                sectionId={MessageSections.LOGIN}
              />
            </BarekamText>

            <BarekamButton
              type={SimpleButtons.LINK}
              onClick={() => navigate("../register")}
            >
              <BarekamText variant={"m"}>
                <Message
                  messageKey={"create_account"}
                  sectionId={MessageSections.LOGIN}
                />
              </BarekamText>
            </BarekamButton>
          </div>
        </InputContainer>
      </LoginContainer>

      <Footer />
    </>
  );
};

export default Login;
